/**
 * ROI Modeler Component
 * Comprehensive ROI modeling with AI predictions, data integration, and scenario comparison
 * Trust Marketplace - Healthcare ROI Analytics
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Users,
  Activity,
  Database,
  Brain,
  Sparkles,
  BarChart3,
  PieChart,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Info,
  Plus,
  Trash2,
  Copy,
  RefreshCw,
  Download,
  Upload,
  Globe,
  Building2,
  FileText,
  Zap,
  Calculator,
  LineChart
} from 'lucide-react';

// =====================================================
// Type Definitions
// =====================================================

interface ROIModelerProps {
  programId?: number;
  programType: string;
  sponsorId: number;
  onConfigChange?: (config: ROIModelConfig) => void;
  onScenarioSelect?: (scenario: ROIScenario) => void;
  onScenariosChange?: (scenarios: ROIScenario[]) => void;
  onDataSourcesChange?: (connectedSources: string[]) => void;
  onScenarioFinalize?: (scenario: ROIScenario) => void;
  initialConfig?: Partial<ROIModelConfig>;
  initialScenarios?: ROIScenario[];
  initialConnectedSources?: string[];
  initialFinalizedScenarioId?: string;
}

interface ROIModelConfig {
  enrollment_target: number;
  total_budget: number;
  program_duration_months: number;
  avg_pmpm_rate: number;
  baseline_pmpm_cost: number;
  target_pmpm_cost: number;
  baseline_ed_visits_per_1000: number;
  target_ed_visits_per_1000: number;
  avg_ed_visit_cost: number;
  baseline_hospital_admits_per_1000: number;
  target_hospital_admits_per_1000: number;
  avg_hospital_admit_cost: number;
  target_retention_rate_6mo: number;
  target_retention_rate_12mo: number;
  admin_overhead_percentage: number;
}

interface ROIScenario {
  id: string;
  name: string;
  description: string;
  config: ROIModelConfig;
  projections: ROIProjections;
  confidence: number;
  source: 'manual' | 'ai' | 'benchmark' | 'historical';
}

interface ROIProjections {
  projected_annual_revenue: number;
  projected_annual_savings: number;
  projected_roi_percentage: number;
  projected_roi_multiple: number;
  cost_per_enrollment: number;
  total_program_cost: number;
  net_benefit: number;
  payback_months: number;
  break_even_enrollments: number;
}

interface DataSource {
  id: string;
  name: string;
  type: 'internal' | 'public' | 'ai';
  status: 'connected' | 'available' | 'loading';
  description: string;
  metrics: string[];
  lastUpdated?: string;
}

interface BenchmarkData {
  metric: string;
  industry_avg: number;
  top_quartile: number;
  your_target: number;
  unit: string;
}

interface ProgramTypeModel {
  type: string;
  label: string;
  defaultConfig: Partial<ROIModelConfig>;
  keyMetrics: string[];
  benchmarks: BenchmarkData[];
}

// =====================================================
// Constants
// =====================================================

const PROGRAM_TYPE_MODELS: Record<string, ProgramTypeModel> = {
  CalAIM_ECM: {
    type: 'CalAIM_ECM',
    label: 'CalAIM Enhanced Care Management',
    defaultConfig: {
      avg_pmpm_rate: 350,
      baseline_pmpm_cost: 850,
      target_pmpm_cost: 680,
      baseline_ed_visits_per_1000: 520,
      target_ed_visits_per_1000: 364,
      baseline_hospital_admits_per_1000: 140,
      target_hospital_admits_per_1000: 98,
      target_retention_rate_6mo: 82,
      target_retention_rate_12mo: 75,
    },
    keyMetrics: ['ED Utilization', 'Inpatient Admits', 'Care Coordination', 'Member Retention'],
    benchmarks: [
      { metric: 'PMPM Cost Reduction', industry_avg: 12, top_quartile: 20, your_target: 20, unit: '%' },
      { metric: 'ED Visit Reduction', industry_avg: 18, top_quartile: 30, your_target: 30, unit: '%' },
      { metric: 'Hospital Admits Reduction', industry_avg: 15, top_quartile: 25, your_target: 30, unit: '%' },
      { metric: '12-Month Retention', industry_avg: 70, top_quartile: 85, your_target: 75, unit: '%' },
    ]
  },
  Medicaid_Enrollment: {
    type: 'Medicaid_Enrollment',
    label: 'Medicaid Enrollment Assistance',
    defaultConfig: {
      avg_pmpm_rate: 150,
      baseline_pmpm_cost: 450,
      target_pmpm_cost: 400,
      baseline_ed_visits_per_1000: 380,
      target_ed_visits_per_1000: 304,
      baseline_hospital_admits_per_1000: 90,
      target_hospital_admits_per_1000: 72,
      target_retention_rate_6mo: 88,
      target_retention_rate_12mo: 82,
    },
    keyMetrics: ['Enrollment Conversion', 'Time to Enrollment', 'Member Activation', 'Cost per Enrollment'],
    benchmarks: [
      { metric: 'Enrollment Conversion Rate', industry_avg: 45, top_quartile: 65, your_target: 60, unit: '%' },
      { metric: 'Avg Days to Enrollment', industry_avg: 28, top_quartile: 14, your_target: 18, unit: 'days' },
      { metric: 'Cost per Successful Enrollment', industry_avg: 350, top_quartile: 200, your_target: 250, unit: '$' },
      { metric: '6-Month Retention', industry_avg: 82, top_quartile: 92, your_target: 88, unit: '%' },
    ]
  },
  Behavioral_Health: {
    type: 'Behavioral_Health',
    label: 'Behavioral Health Integration',
    defaultConfig: {
      avg_pmpm_rate: 280,
      baseline_pmpm_cost: 720,
      target_pmpm_cost: 576,
      baseline_ed_visits_per_1000: 480,
      target_ed_visits_per_1000: 336,
      baseline_hospital_admits_per_1000: 110,
      target_hospital_admits_per_1000: 77,
      target_retention_rate_6mo: 75,
      target_retention_rate_12mo: 68,
    },
    keyMetrics: ['BH Service Utilization', 'Crisis Interventions', 'Medication Adherence', 'Symptom Improvement'],
    benchmarks: [
      { metric: 'BH Visit Completion', industry_avg: 55, top_quartile: 75, your_target: 70, unit: '%' },
      { metric: 'Crisis ED Reduction', industry_avg: 20, top_quartile: 35, your_target: 30, unit: '%' },
      { metric: 'Treatment Adherence', industry_avg: 50, top_quartile: 70, your_target: 65, unit: '%' },
      { metric: 'PHQ-9 Improvement', industry_avg: 35, top_quartile: 55, your_target: 50, unit: '%' },
    ]
  },
  Preventive_Care: {
    type: 'Preventive_Care',
    label: 'Preventive Care & Wellness',
    defaultConfig: {
      avg_pmpm_rate: 120,
      baseline_pmpm_cost: 380,
      target_pmpm_cost: 323,
      baseline_ed_visits_per_1000: 320,
      target_ed_visits_per_1000: 256,
      baseline_hospital_admits_per_1000: 75,
      target_hospital_admits_per_1000: 60,
      target_retention_rate_6mo: 78,
      target_retention_rate_12mo: 72,
    },
    keyMetrics: ['Screenings Completed', 'Care Gap Closure', 'Chronic Disease Management', 'Wellness Engagement'],
    benchmarks: [
      { metric: 'Annual Wellness Visits', industry_avg: 42, top_quartile: 65, your_target: 60, unit: '%' },
      { metric: 'Care Gap Closure Rate', industry_avg: 55, top_quartile: 80, your_target: 75, unit: '%' },
      { metric: 'A1C Control (Diabetics)', industry_avg: 45, top_quartile: 65, your_target: 60, unit: '%' },
      { metric: 'BP Control (Hypertension)', industry_avg: 50, top_quartile: 70, your_target: 65, unit: '%' },
    ]
  },
  CalAIM_CS: {
    type: 'CalAIM_CS',
    label: 'CalAIM Community Supports',
    defaultConfig: {
      avg_pmpm_rate: 200,
      baseline_pmpm_cost: 600,
      target_pmpm_cost: 480,
      baseline_ed_visits_per_1000: 450,
      target_ed_visits_per_1000: 338,
      baseline_hospital_admits_per_1000: 100,
      target_hospital_admits_per_1000: 75,
      target_retention_rate_6mo: 80,
      target_retention_rate_12mo: 73,
    },
    keyMetrics: ['Housing Stability', 'Food Security', 'Transportation Access', 'SDOH Improvement'],
    benchmarks: [
      { metric: 'Housing Placement Rate', industry_avg: 40, top_quartile: 60, your_target: 55, unit: '%' },
      { metric: 'Food Insecurity Reduction', industry_avg: 35, top_quartile: 55, your_target: 50, unit: '%' },
      { metric: 'Transportation Utilization', industry_avg: 60, top_quartile: 85, your_target: 80, unit: '%' },
      { metric: 'Overall SDOH Score Improvement', industry_avg: 25, top_quartile: 40, your_target: 35, unit: '%' },
    ]
  }
};

const DATA_SOURCES: DataSource[] = [
  {
    id: 'integration_workbench',
    name: 'Integration Workbench',
    type: 'internal',
    status: 'connected',
    description: 'Your imported program data, members, and billing activities',
    metrics: ['Historical Enrollments', 'Claims Data', 'Member Demographics', 'Service Utilization']
  },
  {
    id: 'program_history',
    name: 'Historical Program Data',
    type: 'internal',
    status: 'connected',
    description: 'Past program performance and outcomes',
    metrics: ['Prior ROI', 'Retention Rates', 'Cost per Enrollment', 'Outcome Achievement']
  },
  {
    id: 'cms_data',
    name: 'CMS Public Data',
    type: 'public',
    status: 'available',
    description: 'Medicare/Medicaid benchmarks and quality measures',
    metrics: ['HEDIS Benchmarks', 'Star Ratings', 'Utilization Rates', 'Cost Trends']
  },
  {
    id: 'state_health',
    name: 'State Health Data',
    type: 'public',
    status: 'available',
    description: 'State-specific Medicaid and population health data',
    metrics: ['State Medicaid Rates', 'Regional Costs', 'Population Health', 'Provider Availability']
  },
  {
    id: 'census_data',
    name: 'Census & SDOH Data',
    type: 'public',
    status: 'available',
    description: 'Social determinants and demographic data',
    metrics: ['SDOH Indices', 'Demographics', 'Economic Indicators', 'Geographic Factors']
  },
  {
    id: 'ai_predictions',
    name: 'AI Predictive Model',
    type: 'ai',
    status: 'available',
    description: 'Machine learning predictions based on similar programs',
    metrics: ['Enrollment Forecast', 'Cost Predictions', 'Risk Scores', 'Outcome Probability']
  }
];

// =====================================================
// Calculation Functions
// =====================================================

function calculateProjections(config: ROIModelConfig): ROIProjections {
  const annualEnrollments = config.enrollment_target;
  const avgMonthlyEnrollments = annualEnrollments / 12;

  // Revenue calculations
  const projected_annual_revenue = annualEnrollments * config.avg_pmpm_rate * 12;

  // Cost calculations
  const admin_overhead = config.total_budget * (config.admin_overhead_percentage / 100);
  const total_program_cost = config.total_budget + admin_overhead;
  const cost_per_enrollment = total_program_cost / annualEnrollments;

  // Savings calculations
  const baseline_annual_cost_per_member = config.baseline_pmpm_cost * 12;
  const target_annual_cost_per_member = config.target_pmpm_cost * 12;
  const cost_savings_per_member = baseline_annual_cost_per_member - target_annual_cost_per_member;

  // ED visit savings
  const ed_visit_reduction = config.baseline_ed_visits_per_1000 - config.target_ed_visits_per_1000;
  const ed_savings_per_1000 = ed_visit_reduction * config.avg_ed_visit_cost;

  // Hospital admit savings
  const admit_reduction = config.baseline_hospital_admits_per_1000 - config.target_hospital_admits_per_1000;
  const admit_savings_per_1000 = admit_reduction * config.avg_hospital_admit_cost;

  // Total savings
  const utilization_savings_per_1000 = ed_savings_per_1000 + admit_savings_per_1000;
  const utilization_savings = (utilization_savings_per_1000 / 1000) * annualEnrollments;
  const pmpm_savings = cost_savings_per_member * annualEnrollments;
  const projected_annual_savings = Math.max(utilization_savings, pmpm_savings);

  // ROI calculations
  const net_benefit = projected_annual_savings - total_program_cost;
  const projected_roi_percentage = total_program_cost > 0
    ? ((net_benefit / total_program_cost) * 100)
    : 0;
  const projected_roi_multiple = total_program_cost > 0
    ? (projected_annual_savings / total_program_cost)
    : 0;

  // Payback calculations
  const monthly_savings = projected_annual_savings / 12;
  const payback_months = monthly_savings > 0
    ? Math.ceil(total_program_cost / monthly_savings)
    : 0;

  // Break-even
  const savings_per_enrollment = projected_annual_savings / annualEnrollments;
  const break_even_enrollments = savings_per_enrollment > 0
    ? Math.ceil(total_program_cost / savings_per_enrollment)
    : 0;

  return {
    projected_annual_revenue,
    projected_annual_savings,
    projected_roi_percentage,
    projected_roi_multiple,
    cost_per_enrollment,
    total_program_cost,
    net_benefit,
    payback_months,
    break_even_enrollments
  };
}

// =====================================================
// Component
// =====================================================

const ROIModeler: React.FC<ROIModelerProps> = ({
  programId,
  programType,
  sponsorId,
  onConfigChange,
  onScenarioSelect,
  onScenariosChange,
  onDataSourcesChange,
  onScenarioFinalize,
  initialConfig,
  initialScenarios,
  initialConnectedSources,
  initialFinalizedScenarioId
}) => {
  const { t } = useAppTranslation();
  // State
  const [activeTab, setActiveTab] = useState<'model' | 'scenarios' | 'data' | 'pricing'>('model');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Model configuration
  const programModel = PROGRAM_TYPE_MODELS[programType] || PROGRAM_TYPE_MODELS.Medicaid_Enrollment;

  const defaultConfig: ROIModelConfig = {
    enrollment_target: 500,
    total_budget: 250000,
    program_duration_months: 12,
    avg_pmpm_rate: programModel.defaultConfig.avg_pmpm_rate || 200,
    baseline_pmpm_cost: programModel.defaultConfig.baseline_pmpm_cost || 600,
    target_pmpm_cost: programModel.defaultConfig.target_pmpm_cost || 480,
    baseline_ed_visits_per_1000: programModel.defaultConfig.baseline_ed_visits_per_1000 || 400,
    target_ed_visits_per_1000: programModel.defaultConfig.target_ed_visits_per_1000 || 320,
    avg_ed_visit_cost: 1400,
    baseline_hospital_admits_per_1000: programModel.defaultConfig.baseline_hospital_admits_per_1000 || 100,
    target_hospital_admits_per_1000: programModel.defaultConfig.target_hospital_admits_per_1000 || 75,
    avg_hospital_admit_cost: 12000,
    target_retention_rate_6mo: programModel.defaultConfig.target_retention_rate_6mo || 80,
    target_retention_rate_12mo: programModel.defaultConfig.target_retention_rate_12mo || 75,
    admin_overhead_percentage: 10
  };

  const [config, setConfig] = useState<ROIModelConfig>({ ...defaultConfig, ...initialConfig });

  // Load connected sources from localStorage
  const getInitialConnectedSources = () => {
    const storageKey = programId ? `roi_connected_sources_program_${programId}` : `roi_connected_sources_sponsor_${sponsorId}`;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {
      // Fall through to default
    }
    return initialConnectedSources || ['integration_workbench', 'program_history'];
  };

  // Load scenarios from localStorage
  const getInitialScenarios = () => {
    const storageKey = programId ? `roi_scenarios_program_${programId}` : `roi_scenarios_sponsor_${sponsorId}`;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {
      // Fall through to default
    }
    return initialScenarios || [];
  };

  // Load state code from localStorage with fallback to 'CA'
  const getInitialStateCode = () => {
    const storageKey = programId ? `roi_state_code_program_${programId}` : `roi_state_code_sponsor_${sponsorId}`;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved || 'CA';
    } catch {
      return 'CA';
    }
  };

  const [scenarios, setScenarios] = useState<ROIScenario[]>(getInitialScenarios);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [finalizedScenarioId, setFinalizedScenarioId] = useState<string | null>(initialFinalizedScenarioId || null);
  const [connectedSources, setConnectedSources] = useState<string[]>(getInitialConnectedSources);

  const [stateCode, setStateCode] = useState<string>(getInitialStateCode);
  const [externalBenchmarks, setExternalBenchmarks] = useState<any>(null);
  const [loadingBenchmarks, setLoadingBenchmarks] = useState(false);

  // Persist state code to localStorage when it changes
  useEffect(() => {
    const storageKey = programId ? `roi_state_code_program_${programId}` : `roi_state_code_sponsor_${sponsorId}`;
    try {
      localStorage.setItem(storageKey, stateCode);
    } catch (err) {
      console.error('Failed to save state code to localStorage:', err);
    }
  }, [stateCode, programId, sponsorId]);

  // Persist connected sources to localStorage when they change
  useEffect(() => {
    const storageKey = programId ? `roi_connected_sources_program_${programId}` : `roi_connected_sources_sponsor_${sponsorId}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(connectedSources));
    } catch (err) {
      console.error('Failed to save connected sources to localStorage:', err);
    }
  }, [connectedSources, programId, sponsorId]);

  // Persist scenarios to localStorage when they change
  useEffect(() => {
    const storageKey = programId ? `roi_scenarios_program_${programId}` : `roi_scenarios_sponsor_${sponsorId}`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(scenarios));
    } catch (err) {
      console.error('Failed to save scenarios to localStorage:', err);
    }
  }, [scenarios, programId, sponsorId]);

  // Notify parent when scenarios change
  useEffect(() => {
    onScenariosChange?.(scenarios);
  }, [scenarios, onScenariosChange]);

  // Notify parent when connected sources change
  useEffect(() => {
    onDataSourcesChange?.(connectedSources);
  }, [connectedSources, onDataSourcesChange]);

  // Fetch external benchmarks when sources are connected
  useEffect(() => {
    const fetchExternalData = async () => {
      const hasCms = connectedSources.includes('cms_data');
      const hasState = connectedSources.includes('state_health');
      const hasSdoh = connectedSources.includes('census_data');

      if (!hasCms && !hasState && !hasSdoh) {
        setExternalBenchmarks(null);
        return;
      }

      setLoadingBenchmarks(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(
          getWeaveinApiUrl(`/api/roi/data-sources/aggregate?state_code=${stateCode}&program_type=${programType}`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': localStorage.getItem('active_cbo_id') || ''
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          setExternalBenchmarks(data);
        }
      } catch (err) {
        console.error('Failed to fetch external benchmarks:', err);
      } finally {
        setLoadingBenchmarks(false);
      }
    };

    fetchExternalData();
  }, [connectedSources, stateCode, programType]);

  // Calculate projections
  const projections = useMemo(() => calculateProjections(config), [config]);

  // Initialize with base scenario only if no initial scenarios provided
  useEffect(() => {
    if (scenarios.length === 0 && !initialScenarios?.length) {
      const baseScenario: ROIScenario = {
        id: 'base',
        name: 'Base Case',
        description: 'Standard projections based on program type benchmarks',
        config: { ...config },
        projections: calculateProjections(config),
        confidence: 75,
        source: 'benchmark'
      };
      setScenarios([baseScenario]);
      setSelectedScenarioId('base');
    } else if (scenarios.length > 0 && !selectedScenarioId) {
      setSelectedScenarioId(scenarios[0].id);
    }
  }, []);

  // Update config handler
  const handleConfigChange = (field: keyof ROIModelConfig, value: number) => {
    const newConfig = { ...config, [field]: value };
    setConfig(newConfig);
    onConfigChange?.(newConfig);

    // Update base scenario
    setScenarios(prev => prev.map(s =>
      s.id === 'base'
        ? { ...s, config: newConfig, projections: calculateProjections(newConfig) }
        : s
    ));
  };

  // Generate AI scenario using enhanced backend with external data
  const generateAIScenario = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');

      // Build query params for connected sources
      const sourcesParam = connectedSources.map(s => `connected_sources=${s}`).join('&');

      // Call enhanced AI scenario endpoint with external data integration
      const response = await fetch(
        getWeaveinApiUrl(`/api/roi/ai/enhanced-scenarios?state_code=${stateCode}&${sourcesParam}`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || ''
          },
          body: JSON.stringify({
            program_type: programType,
            enrollment_target: config.enrollment_target,
            total_budget: config.total_budget,
            baseline_pmpm_cost: config.baseline_pmpm_cost,
            target_pmpm_cost: config.target_pmpm_cost,
            baseline_ed_visits_per_1000: config.baseline_ed_visits_per_1000,
            target_ed_visits_per_1000: config.target_ed_visits_per_1000,
            baseline_hospital_admits_per_1000: config.baseline_hospital_admits_per_1000,
            target_hospital_admits_per_1000: config.target_hospital_admits_per_1000,
            target_retention_rate_12mo: config.target_retention_rate_12mo
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate AI scenarios');
      }

      const data = await response.json();

      // Convert backend scenarios to frontend format
      const aiScenarios: ROIScenario[] = data.scenarios.map((s: any, idx: number) => {
        const adjustedConfig: ROIModelConfig = {
          ...config,
          enrollment_target: s.config_adjustments.enrollment_target || config.enrollment_target,
          target_pmpm_cost: s.config_adjustments.target_pmpm_cost || config.target_pmpm_cost,
          target_ed_visits_per_1000: s.config_adjustments.target_ed_visits_per_1000 || config.target_ed_visits_per_1000,
          target_hospital_admits_per_1000: s.config_adjustments.target_hospital_admits_per_1000 || config.target_hospital_admits_per_1000,
          target_retention_rate_12mo: s.config_adjustments.target_retention_rate_12mo || config.target_retention_rate_12mo,
        };

        return {
          id: `ai-${s.scenario_type}-${Date.now()}-${idx}`,
          name: s.scenario_name,
          description: s.risk_factors.join('. ') + '. ' + s.recommendations[0],
          config: adjustedConfig,
          projections: calculateProjections(adjustedConfig),
          confidence: s.confidence_score,
          source: 'ai' as const
        };
      });

      setScenarios(prev => [...prev, ...aiScenarios]);

      // Show enhancement info
      if (data.enhancements_applied) {
        const enhancements = [];
        if (data.enhancements_applied.cms_benchmarks) enhancements.push('CMS benchmarks');
        if (data.enhancements_applied.state_health_data) enhancements.push(`${stateCode} state data`);
        if (data.enhancements_applied.sdoh_factors) enhancements.push('SDOH risk factors');
        if (enhancements.length > 0) {
          console.log(`✅ AI scenarios enhanced with: ${enhancements.join(', ')}`);
        }
      }
    } catch (err) {
      console.error('AI scenario generation error:', err);
      setError('Failed to generate AI scenarios. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Add custom scenario
  const addCustomScenario = () => {
    const newScenario: ROIScenario = {
      id: `custom-${Date.now()}`,
      name: `Custom Scenario ${scenarios.length}`,
      description: 'Custom scenario for comparison',
      config: { ...config },
      projections: calculateProjections(config),
      confidence: 70,
      source: 'manual'
    };
    setScenarios(prev => [...prev, newScenario]);
    setSelectedScenarioId(newScenario.id);
  };

  // Delete scenario
  const deleteScenario = (id: string) => {
    if (id === 'base') return; // Can't delete base
    setScenarios(prev => prev.filter(s => s.id !== id));
    if (selectedScenarioId === id) {
      setSelectedScenarioId('base');
    }
  };

  // Finalize scenario - mark as accepted for ROI tracking
  const finalizeScenario = async (scenario: ROIScenario) => {
    setFinalizedScenarioId(scenario.id);
    onScenarioFinalize?.(scenario);

    // Save to backend if programId is available
    if (programId) {
      try {
        setLoading(true);
        const token = localStorage.getItem('auth_token');
        await fetch(getWeaveinApiUrl(`/api/roi/programs/${programId}/finalize-scenario`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': localStorage.getItem('active_cbo_id') || ''
          },
          body: JSON.stringify({
            scenario_id: scenario.id,
            scenario_name: scenario.name,
            config: scenario.config,
            projections: scenario.projections
          })
        });
      } catch (err) {
        console.error('Failed to save finalized scenario:', err);
        setError('Failed to save finalized scenario');
      } finally {
        setLoading(false);
      }
    }
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Get ROI color
  const getROIColor = (roi: number) => {
    if (roi >= 100) return 'text-green-600';
    if (roi >= 50) return 'text-blue-600';
    if (roi >= 0) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('sponsors.roi_modeler', 'ROI Modeler')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('sponsors.build_and_compare_roi_scenarios', 'Build and compare ROI scenarios with AI-powered projections')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={generateAIScenario} disabled={loading}>
            {loading ? <LoadingSpinner /> : <Brain className="w-4 h-4 mr-2" />}
            Generate AI Scenarios
          </Button>
          <Button variant="outline" size="sm" onClick={addCustomScenario}>
            <Plus className="w-4 h-4 mr-2" />
            {t('sponsors.add_scenario', 'Add Scenario')}
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Card className="p-4 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          {[
            { id: 'model', label: 'Model Configuration', icon: Calculator },
            { id: 'scenarios', label: 'Scenario Comparison', icon: BarChart3 },
            { id: 'data', label: 'Data Sources', icon: Database },
            { id: 'pricing', label: 'Pricing Impact', icon: DollarSign }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:bg-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'model' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuration Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Enrollment & Budget */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                {t('sponsors.enrollment_budget', 'Enrollment & Budget')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('sponsors.enrollment_target', 'Enrollment Target')}</Label>
                  <Input
                    type="number"
                    value={config.enrollment_target}
                    onChange={e => handleConfigChange('enrollment_target', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label>{t('sponsors.total_budget', 'Total Budget ($)')}</Label>
                  <Input
                    type="number"
                    value={config.total_budget}
                    onChange={e => handleConfigChange('total_budget', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label>Program Duration (months)</Label>
                  <Input
                    type="number"
                    value={config.program_duration_months}
                    onChange={e => handleConfigChange('program_duration_months', parseInt(e.target.value) || 12)}
                  />
                </div>
                <div>
                  <Label>{t('sponsors.admin_overhead', 'Admin Overhead (%)')}</Label>
                  <Input
                    type="number"
                    value={config.admin_overhead_percentage}
                    onChange={e => handleConfigChange('admin_overhead_percentage', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            </Card>

            {/* PMPM Rates */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                {t('sponsors.pmpm_cost_model', 'PMPM Cost Model')}
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>{t('sponsors.avg_pmpm_rate', 'Avg PMPM Rate ($)')}</Label>
                  <Input
                    type="number"
                    value={config.avg_pmpm_rate}
                    onChange={e => handleConfigChange('avg_pmpm_rate', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label>Baseline PMPM Cost ($)</Label>
                  <Input
                    type="number"
                    value={config.baseline_pmpm_cost}
                    onChange={e => handleConfigChange('baseline_pmpm_cost', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label>{t('sponsors.target_pmpm_cost', 'Target PMPM Cost ($)')}</Label>
                  <Input
                    type="number"
                    value={config.target_pmpm_cost}
                    onChange={e => handleConfigChange('target_pmpm_cost', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-700 dark:text-blue-300">{t('sponsors.projected_cost_reduction', 'Projected Cost Reduction:')}</span>
                  <span className="font-bold text-blue-900 dark:text-blue-200">
                    {formatPercent(((config.baseline_pmpm_cost - config.target_pmpm_cost) / config.baseline_pmpm_cost) * 100)}
                  </span>
                </div>
              </div>
            </Card>

            {/* Utilization Targets */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                {t('sponsors.utilization_targets', 'Utilization Targets')}
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>{t('sponsors.baseline_ed_visits1000', 'Baseline ED Visits/1000')}</Label>
                    <Input
                      type="number"
                      value={config.baseline_ed_visits_per_1000}
                      onChange={e => handleConfigChange('baseline_ed_visits_per_1000', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>{t('sponsors.target_ed_visits1000', 'Target ED Visits/1000')}</Label>
                    <Input
                      type="number"
                      value={config.target_ed_visits_per_1000}
                      onChange={e => handleConfigChange('target_ed_visits_per_1000', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Avg ED Visit Cost ($)</Label>
                    <Input
                      type="number"
                      value={config.avg_ed_visit_cost}
                      onChange={e => handleConfigChange('avg_ed_visit_cost', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>{t('sponsors.baseline_admits1000', 'Baseline Admits/1000')}</Label>
                    <Input
                      type="number"
                      value={config.baseline_hospital_admits_per_1000}
                      onChange={e => handleConfigChange('baseline_hospital_admits_per_1000', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>{t('sponsors.target_admits1000', 'Target Admits/1000')}</Label>
                    <Input
                      type="number"
                      value={config.target_hospital_admits_per_1000}
                      onChange={e => handleConfigChange('target_hospital_admits_per_1000', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>{t('sponsors.avg_admit_cost', 'Avg Admit Cost ($)')}</Label>
                    <Input
                      type="number"
                      value={config.avg_hospital_admit_cost}
                      onChange={e => handleConfigChange('avg_hospital_admit_cost', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Retention Targets */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-600" />
                {t('sponsors.retention_targets', 'Retention Targets')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>6-Month Retention (%)</Label>
                  <Input
                    type="number"
                    value={config.target_retention_rate_6mo}
                    onChange={e => handleConfigChange('target_retention_rate_6mo', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label>12-Month Retention (%)</Label>
                  <Input
                    type="number"
                    value={config.target_retention_rate_12mo}
                    onChange={e => handleConfigChange('target_retention_rate_12mo', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Real-time Projections Panel */}
          <div className="space-y-6">
            {/* Key Metrics */}
            <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 border-blue-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('sponsors.realtime_projections', 'Real-time Projections')}</h3>

              <div className="space-y-4">
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('sponsors.projected_annual_savings', 'Projected Annual Savings')}</div>
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(projections.projected_annual_savings)}
                  </div>
                </div>

                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('sponsors.roi_percentage', 'ROI Percentage')}</div>
                  <div className={`text-2xl font-bold ${getROIColor(projections.projected_roi_percentage)}`}>
                    {formatPercent(projections.projected_roi_percentage)}
                  </div>
                </div>

                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('sponsors.roi_multiple', 'ROI Multiple')}</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {projections.projected_roi_multiple.toFixed(2)}x
                  </div>
                </div>

                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('sponsors.net_benefit', 'Net Benefit')}</div>
                  <div className={`text-2xl font-bold ${projections.net_benefit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(projections.net_benefit)}
                  </div>
                </div>

                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('sponsors.cost_per_enrollment', 'Cost per Enrollment')}</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(projections.cost_per_enrollment)}
                  </div>
                </div>

                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('sponsors.payback_period', 'Payback Period')}</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {projections.payback_months} months
                  </div>
                </div>

                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('sponsors.breakeven_enrollments', 'Break-even Enrollments')}</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {projections.break_even_enrollments.toLocaleString()}
                  </div>
                </div>
              </div>
            </Card>

            {/* Benchmarks */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                {t('sponsors.industry_benchmarks', 'Industry Benchmarks')}
              </h3>
              <div className="space-y-3">
                {programModel.benchmarks.map((benchmark, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{benchmark.metric}</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {benchmark.your_target}{benchmark.unit}
                      </span>
                    </div>
                    <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                      <div
                        className="absolute h-2 bg-blue-600 rounded-full"
                        style={{ width: `${Math.min((benchmark.your_target / benchmark.top_quartile) * 100, 100)}%` }}
                      />
                      <div
                        className="absolute w-0.5 h-3 bg-yellow-500 -top-0.5"
                        style={{ left: `${(benchmark.industry_avg / benchmark.top_quartile) * 100}%` }}
                        title={`Industry Avg: ${benchmark.industry_avg}${benchmark.unit}`}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>0</span>
                      <span>Top: {benchmark.top_quartile}{benchmark.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'scenarios' && (
        <div className="space-y-6">
          {/* Scenario Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scenarios.map(scenario => (
              <Card
                key={scenario.id}
                className={`p-5 cursor-pointer transition-all hover:shadow-lg ${
                  selectedScenarioId === scenario.id ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/30' : ''
                }`}
                onClick={() => {
                  setSelectedScenarioId(scenario.id);
                  onScenarioSelect?.(scenario);
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">{scenario.name}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{scenario.description}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {scenario.source === 'ai' && (
                      <Badge className="bg-purple-100 text-purple-800">
                        <Brain className="w-3 h-3 mr-1" />
                        AI
                      </Badge>
                    )}
                    {scenario.source === 'benchmark' && (
                      <Badge className="bg-blue-100 text-blue-800">
                        <BarChart3 className="w-3 h-3 mr-1" />
                        {t('sponsors.benchmark', 'Benchmark')}
                      </Badge>
                    )}
                    {scenario.id !== 'base' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteScenario(scenario.id);
                        }}
                        className="text-red-500 hover:text-red-700 p-1 h-auto"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">ROI</span>
                    <span className={`font-bold ${getROIColor(scenario.projections.projected_roi_percentage)}`}>
                      {formatPercent(scenario.projections.projected_roi_percentage)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">{t('sponsors.savings', 'Savings')}</span>
                    <span className="font-semibold text-green-600">
                      {formatCurrency(scenario.projections.projected_annual_savings)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">{t('sponsors.net_benefit_1', 'Net Benefit')}</span>
                    <span className={`font-semibold ${scenario.projections.net_benefit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(scenario.projections.net_benefit)}
                    </span>
                  </div>
                  <div className="pt-2 border-t dark:border-gray-700">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-400">{t('sponsors.confidence', 'Confidence')}</span>
                      <span className="text-gray-600 dark:text-gray-300">{scenario.confidence}%</span>
                    </div>
                    <Progress value={scenario.confidence} className="h-1 mt-1" />
                  </div>

                  {/* Accept as Final / Finalized Badge */}
                  <div className="pt-3 mt-3 border-t dark:border-gray-700">
                    {finalizedScenarioId === scenario.id ? (
                      <div className="flex items-center justify-center gap-2 py-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-700 dark:text-green-300">{t('sponsors.accepted_as_final', 'Accepted as Final')}</span>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30"
                        onClick={(e) => {
                          e.stopPropagation();
                          finalizeScenario(scenario);
                        }}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        {t('sponsors.accept_as_final', 'Accept as Final')}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Comparison Table */}
          {scenarios.length > 1 && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('sponsors.scenario_comparison', 'Scenario Comparison')}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b dark:border-gray-700">
                      <th className="text-left py-2 px-3 dark:text-white">{t('sponsors.metric', 'Metric')}</th>
                      {scenarios.map(s => (
                        <th key={s.id} className="text-right py-2 px-3 dark:text-white">{s.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Scenario Assumptions Section */}
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      <td colSpan={scenarios.length + 1} className="py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        {t('sponsors.scenario_assumptions', 'Scenario Assumptions')}
                      </td>
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.enrollments', 'Enrollments')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium">
                          {s.config.enrollment_target.toLocaleString()}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.target_pmpm_cost_1', 'Target PMPM Cost')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium dark:text-white">
                          ${s.config.target_pmpm_cost.toLocaleString()}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.target_ed_visits1000_1', 'Target ED Visits/1000')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium dark:text-white">
                          {s.config.target_ed_visits_per_1000}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.target_admits1000_1', 'Target Admits/1000')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium dark:text-white">
                          {s.config.target_hospital_admits_per_1000}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.target_retention', 'Target Retention')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium dark:text-white">
                          {s.config.target_retention_rate_12mo}%
                        </td>
                      ))}
                    </tr>

                    {/* Financial Projections Section */}
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      <td colSpan={scenarios.length + 1} className="py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        {t('sponsors.financial_projections', 'Financial Projections')}
                      </td>
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.annual_revenue', 'Annual Revenue')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium text-blue-600">
                          {formatCurrency(s.projections.projected_annual_revenue)}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.total_program_cost', 'Total Program Cost')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium dark:text-white">
                          {formatCurrency(s.projections.total_program_cost)}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.cost_per_enrollment_1', 'Cost per Enrollment')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium dark:text-white">
                          {formatCurrency(s.projections.cost_per_enrollment)}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.annual_savings', 'Annual Savings')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium text-green-600">
                          {formatCurrency(s.projections.projected_annual_savings)}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.net_benefit_2', 'Net Benefit')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className={`text-right py-2 px-3 font-medium ${s.projections.net_benefit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(s.projections.net_benefit)}
                        </td>
                      ))}
                    </tr>

                    {/* ROI Metrics Section */}
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      <td colSpan={scenarios.length + 1} className="py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        {t('sponsors.roi_metrics', 'ROI Metrics')}
                      </td>
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">ROI %</td>
                      {scenarios.map(s => (
                        <td key={s.id} className={`text-right py-2 px-3 font-bold ${getROIColor(s.projections.projected_roi_percentage)}`}>
                          {formatPercent(s.projections.projected_roi_percentage)}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.roi_multiple_1', 'ROI Multiple')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium dark:text-white">
                          {s.projections.projected_roi_multiple.toFixed(2)}x
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b dark:border-gray-700">
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.payback_months', 'Payback (months)')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium dark:text-white">
                          {s.projections.payback_months}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{t('sponsors.breakeven_enrollments_1', 'Break-even Enrollments')}</td>
                      {scenarios.map(s => (
                        <td key={s.id} className="text-right py-2 px-3 font-medium dark:text-white">
                          {s.projections.break_even_enrollments.toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'data' && (
        <div className="space-y-6">
          {/* State Selection for Regional Data */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-green-600" />
              {t('sponsors.regional_configuration', 'Regional Configuration')}
            </h3>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label>{t('sponsors.state_code', 'State Code')}</Label>
                <select
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="CA">{t('sponsors.california_ca', 'California (CA)')}</option>
                  <option value="TX">{t('sponsors.texas_tx', 'Texas (TX)')}</option>
                  <option value="FL">{t('sponsors.florida_fl', 'Florida (FL)')}</option>
                  <option value="NY">{t('sponsors.new_york_ny', 'New York (NY)')}</option>
                  <option value="PA">{t('sponsors.pennsylvania_pa', 'Pennsylvania (PA)')}</option>
                  <option value="IL">{t('sponsors.illinois_il', 'Illinois (IL)')}</option>
                  <option value="OH">{t('sponsors.ohio_oh', 'Ohio (OH)')}</option>
                  <option value="GA">{t('sponsors.georgia_ga', 'Georgia (GA)')}</option>
                  <option value="NC">{t('sponsors.north_carolina_nc', 'North Carolina (NC)')}</option>
                  <option value="MI">{t('sponsors.michigan_mi', 'Michigan (MI)')}</option>
                  <option value="NJ">{t('sponsors.new_jersey_nj', 'New Jersey (NJ)')}</option>
                  <option value="VA">{t('sponsors.virginia_va', 'Virginia (VA)')}</option>
                  <option value="WA">{t('sponsors.washington_wa', 'Washington (WA)')}</option>
                  <option value="AZ">{t('sponsors.arizona_az', 'Arizona (AZ)')}</option>
                  <option value="MA">{t('sponsors.massachusetts_ma', 'Massachusetts (MA)')}</option>
                  <option value="TN">{t('sponsors.tennessee_tn', 'Tennessee (TN)')}</option>
                  <option value="IN">{t('sponsors.indiana_in', 'Indiana (IN)')}</option>
                  <option value="MO">{t('sponsors.missouri_mo', 'Missouri (MO)')}</option>
                  <option value="MD">{t('sponsors.maryland_md', 'Maryland (MD)')}</option>
                  <option value="WI">{t('sponsors.wisconsin_wi', 'Wisconsin (WI)')}</option>
                </select>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p>{t('sponsors.select_your_state_to_get', 'Select your state to get regional healthcare benchmarks, costs, and SDOH data.')}</p>
              </div>
            </div>
          </Card>

          {/* Data Sources */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              {t('sponsors.data_sources', 'Data Sources')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('sponsors.connect_data_sources_to_improve', 'Connect data sources to improve model accuracy and generate predictions')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {DATA_SOURCES.map(source => {
                const isConnected = connectedSources.includes(source.id);
                return (
                  <Card
                    key={source.id}
                    className={`p-4 ${isConnected ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700' : 'bg-gray-50 dark:bg-gray-700'}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {source.type === 'internal' && <Building2 className="w-4 h-4 text-blue-600" />}
                        {source.type === 'public' && <Globe className="w-4 h-4 text-green-600" />}
                        {source.type === 'ai' && <Brain className="w-4 h-4 text-purple-600" />}
                        <h4 className="font-semibold text-gray-900 dark:text-white">{source.name}</h4>
                      </div>
                      <Badge className={isConnected ? 'bg-green-100 text-green-800' : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}>
                        {isConnected ? 'Connected' : 'Available'}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{source.description}</p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {source.metrics.map((metric, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {metric}
                        </Badge>
                      ))}
                    </div>
                    <Button
                      variant={isConnected ? 'outline' : 'default'}
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        if (isConnected) {
                          setConnectedSources(prev => prev.filter(id => id !== source.id));
                        } else {
                          setConnectedSources(prev => [...prev, source.id]);
                        }
                      }}
                    >
                      {isConnected ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          {t('sponsors.disconnect', 'Disconnect')}
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          {t('sponsors.connect', 'Connect')}
                        </>
                      )}
                    </Button>
                  </Card>
                );
              })}
            </div>
          </Card>

          {/* Data Quality */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('sponsors.data_quality_score', 'Data Quality Score')}</h3>
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <Progress value={(connectedSources.length / DATA_SOURCES.length) * 100} className="h-3" />
              </div>
              <span className="text-2xl font-bold text-blue-600">
                {Math.round((connectedSources.length / DATA_SOURCES.length) * 100)}%
              </span>
            </div>
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('sponsors.connect_more_data_sources_to', 'Connect more data sources to improve prediction accuracy and model confidence.')}
              </p>
              {connectedSources.length < 4 && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                    Connect {4 - connectedSources.length} more source{4 - connectedSources.length > 1 ? 's' : ''} to enable AI Predictive Model
                  </p>
                  <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                    <p className="font-medium">{t('sponsors.recommended_sources_to_connect', 'Recommended sources to connect:')}</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {!connectedSources.includes('cms_data') && (
                        <li><strong>{t('sponsors.cms_public_data', 'CMS Public Data')}</strong> - HEDIS benchmarks, Star Ratings, utilization rates</li>
                      )}
                      {!connectedSources.includes('state_health') && (
                        <li><strong>{t('sponsors.state_health_data', 'State Health Data')}</strong> - State Medicaid rates, regional costs, provider availability</li>
                      )}
                      {!connectedSources.includes('census_data') && (
                        <li><strong>{t('sponsors.census_sdoh_data', 'Census & SDOH Data')}</strong> - Social determinants, demographics, economic indicators</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
              {connectedSources.length >= 4 && !connectedSources.includes('ai_predictions') && (
                <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-3">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                    <CheckCircle2 className="w-4 h-4 inline mr-1" />
                    {t('sponsors.ready_for_ai_predictions_connect', 'Ready for AI Predictions! Connect the AI Predictive Model for machine learning-powered forecasts.')}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* External Benchmark Data Display */}
          {externalBenchmarks && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                Connected Data Insights
                {loadingBenchmarks && <LoadingSpinner />}
              </h3>

              {/* Recommended Model Inputs */}
              {externalBenchmarks.recommended_model_inputs && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-3">{t('sponsors.airecommended_model_inputs', 'AI-Recommended Model Inputs')}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                      <p className="text-xs text-blue-600 dark:text-blue-300">{t('sponsors.baseline_ed1000', 'Baseline ED/1000')}</p>
                      <p className="text-lg font-bold text-blue-900 dark:text-blue-200">
                        {externalBenchmarks.recommended_model_inputs.baseline_ed_visits_per_1000}
                      </p>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                      <p className="text-xs text-green-600 dark:text-green-300">{t('sponsors.target_ed1000', 'Target ED/1000')}</p>
                      <p className="text-lg font-bold text-green-900 dark:text-green-200">
                        {externalBenchmarks.recommended_model_inputs.target_ed_visits_per_1000}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                      <p className="text-xs text-blue-600 dark:text-blue-300">{t('sponsors.baseline_pmpm', 'Baseline PMPM')}</p>
                      <p className="text-lg font-bold text-blue-900 dark:text-blue-200">
                        ${externalBenchmarks.recommended_model_inputs.baseline_pmpm_cost}
                      </p>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                      <p className="text-xs text-green-600 dark:text-green-300">{t('sponsors.target_pmpm', 'Target PMPM')}</p>
                      <p className="text-lg font-bold text-green-900 dark:text-green-200">
                        ${externalBenchmarks.recommended_model_inputs.target_pmpm_cost}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-4">
                    <Badge className={
                      externalBenchmarks.sdoh_overall_risk === 'low' ? 'bg-green-100 text-green-800' :
                      externalBenchmarks.sdoh_overall_risk === 'high' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }>
                      SDOH Risk: {externalBenchmarks.sdoh_overall_risk}
                    </Badge>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Confidence: {externalBenchmarks.recommended_model_inputs.confidence_score}%
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const rec = externalBenchmarks.recommended_model_inputs;
                        setConfig(prev => ({
                          ...prev,
                          baseline_ed_visits_per_1000: rec.baseline_ed_visits_per_1000,
                          target_ed_visits_per_1000: rec.target_ed_visits_per_1000,
                          baseline_pmpm_cost: rec.baseline_pmpm_cost,
                          target_pmpm_cost: rec.target_pmpm_cost,
                          avg_ed_visit_cost: rec.avg_ed_visit_cost,
                          avg_hospital_admit_cost: rec.avg_hospital_admit_cost
                        }));
                      }}
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      {t('sponsors.apply_to_model', 'Apply to Model')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Data Source Summaries */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* CMS Data */}
                {externalBenchmarks.data_summary?.cms && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {t('sponsors.cms_benchmarks', 'CMS Benchmarks')}
                    </h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {externalBenchmarks.cms_benchmarks_count} measures loaded
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Updated: {externalBenchmarks.data_summary.cms.last_updated}
                    </p>
                  </div>
                )}

                {/* State Data */}
                {externalBenchmarks.data_summary?.state_health && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      {stateCode} Health Data
                    </h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {externalBenchmarks.state_metrics_count} metrics loaded
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Updated: {externalBenchmarks.data_summary.state_health.last_updated}
                    </p>
                  </div>
                )}

                {/* SDOH Data */}
                {externalBenchmarks.data_summary?.sdoh && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-1">
                      <Globe className="w-4 h-4" />
                      {t('sponsors.sdoh_data', 'SDOH Data')}
                    </h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {externalBenchmarks.sdoh_metrics_count} factors analyzed
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      High risk factors: {externalBenchmarks.data_summary.sdoh.high_risk_factors}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'pricing' && (
        <div className="space-y-6">
          {/* Pricing Recommendations */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              {t('sponsors.pricing_recommendations', 'Pricing Recommendations')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('sponsors.based_on_your_roi_model', 'Based on your ROI model, here are suggested pricing tiers for CBO payments')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Conservative Pricing */}
              <Card className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-700">
                <h4 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">{t('sponsors.conservative', 'Conservative')}</h4>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-3">{t('sponsors.lower_risk_guaranteed_roi', 'Lower risk, guaranteed ROI')}</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-yellow-800 dark:text-yellow-300">{t('sponsors.per_enrollment', 'Per Enrollment:')}</span>
                    <span className="font-bold text-yellow-900 dark:text-yellow-200">
                      {formatCurrency(projections.cost_per_enrollment * 0.7)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-yellow-800 dark:text-yellow-300">{t('sponsors.expected_roi', 'Expected ROI:')}</span>
                    <span className="font-bold text-yellow-900 dark:text-yellow-200">
                      {formatPercent(projections.projected_roi_percentage * 1.3)}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Standard Pricing */}
              <Card className="p-4 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 ring-2 ring-blue-300 dark:ring-blue-600">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-200">{t('sponsors.standard', 'Standard')}</h4>
                  <Badge className="bg-blue-100 text-blue-800">{t('sponsors.recommended', 'Recommended')}</Badge>
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">{t('sponsors.balanced_risk_and_reward', 'Balanced risk and reward')}</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-800 dark:text-blue-300">{t('sponsors.per_enrollment_1', 'Per Enrollment:')}</span>
                    <span className="font-bold text-blue-900 dark:text-blue-200">
                      {formatCurrency(projections.cost_per_enrollment)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-800 dark:text-blue-300">{t('sponsors.expected_roi_1', 'Expected ROI:')}</span>
                    <span className="font-bold text-blue-900 dark:text-blue-200">
                      {formatPercent(projections.projected_roi_percentage)}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Aggressive Pricing */}
              <Card className="p-4 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700">
                <h4 className="font-semibold text-green-900 dark:text-green-200 mb-2">{t('sponsors.aggressive', 'Aggressive')}</h4>
                <p className="text-xs text-green-700 dark:text-green-300 mb-3">{t('sponsors.higher_cbo_incentives', 'Higher CBO incentives')}</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-green-800 dark:text-green-300">{t('sponsors.per_enrollment_2', 'Per Enrollment:')}</span>
                    <span className="font-bold text-green-900 dark:text-green-200">
                      {formatCurrency(projections.cost_per_enrollment * 1.3)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-green-800 dark:text-green-300">{t('sponsors.expected_roi_2', 'Expected ROI:')}</span>
                    <span className="font-bold text-green-900 dark:text-green-200">
                      {formatPercent(projections.projected_roi_percentage * 0.7)}
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </Card>

          {/* Performance Thresholds */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('sponsors.performance_thresholds', 'Performance Thresholds')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('sponsors.suggested_contractual_performance_thresholds_based', 'Suggested contractual performance thresholds based on your model')}
            </p>

            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900 dark:text-white">{t('sponsors.minimum_acceptable', 'Minimum Acceptable')}</span>
                  <Badge variant="outline">{t('sponsors.floor', 'Floor')}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('sponsors.enrollments_1', 'Enrollments:')}</span>
                    <span className="ml-2 font-medium dark:text-white">{Math.round(config.enrollment_target * 0.7).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('sponsors.retention', 'Retention:')}</span>
                    <span className="ml-2 font-medium dark:text-white">{Math.round(config.target_retention_rate_12mo * 0.85)}%</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">{t('sponsors.cost_reduction', 'Cost Reduction:')}</span>
                    <span className="ml-2 font-medium dark:text-white">
                      {Math.round(((config.baseline_pmpm_cost - config.target_pmpm_cost) / config.baseline_pmpm_cost) * 100 * 0.7)}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-blue-900 dark:text-blue-200">{t('sponsors.target_performance', 'Target Performance')}</span>
                  <Badge className="bg-blue-100 text-blue-800">{t('sponsors.expected', 'Expected')}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700 dark:text-blue-300">{t('sponsors.enrollments_2', 'Enrollments:')}</span>
                    <span className="ml-2 font-medium text-blue-900 dark:text-blue-200">{config.enrollment_target.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-blue-700 dark:text-blue-300">{t('sponsors.retention_1', 'Retention:')}</span>
                    <span className="ml-2 font-medium text-blue-900 dark:text-blue-200">{config.target_retention_rate_12mo}%</span>
                  </div>
                  <div>
                    <span className="text-blue-700 dark:text-blue-300">{t('sponsors.cost_reduction_1', 'Cost Reduction:')}</span>
                    <span className="ml-2 font-medium text-blue-900 dark:text-blue-200">
                      {Math.round(((config.baseline_pmpm_cost - config.target_pmpm_cost) / config.baseline_pmpm_cost) * 100)}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-green-50 dark:bg-green-900/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-green-900 dark:text-green-200">{t('sponsors.stretch_goal', 'Stretch Goal')}</span>
                  <Badge className="bg-green-100 text-green-800">{t('sponsors.bonus_tier', 'Bonus Tier')}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-green-700 dark:text-green-300">{t('sponsors.enrollments_3', 'Enrollments:')}</span>
                    <span className="ml-2 font-medium text-green-900 dark:text-green-200">{Math.round(config.enrollment_target * 1.2).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-green-700 dark:text-green-300">{t('sponsors.retention_2', 'Retention:')}</span>
                    <span className="ml-2 font-medium text-green-900 dark:text-green-200">{Math.min(95, Math.round(config.target_retention_rate_12mo * 1.1))}%</span>
                  </div>
                  <div>
                    <span className="text-green-700 dark:text-green-300">{t('sponsors.cost_reduction_2', 'Cost Reduction:')}</span>
                    <span className="ml-2 font-medium text-green-900 dark:text-green-200">
                      {Math.round(((config.baseline_pmpm_cost - config.target_pmpm_cost) / config.baseline_pmpm_cost) * 100 * 1.2)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ROIModeler;
